/* The left drawer's panels: Upload, Designs, Auto nest, Background removal
   (coming soon), Settings. Add text lives in TextPanel.tsx (loaded on demand). */

import { lazy, Suspense, useState, type ReactNode } from 'react';
import {
  formatMoney, formatMetres, formatLength, requestedCount, effectiveDpi, unitDecimals, inkGapFor,
  type Design, type Unit
} from '@gsb/core';
import {
  useStore, setDesignSize, setQuantity, removeDesigns, duplicateDesigns, setDesignFlag, renameDesign, toggleDesignOpen, setLockAspect, resetDesignProportions, aspectOf, stretchText,
  setGap, setAllowRotation, setUnit, build, addSheet, addCopy, autoSize, round1, rename, flushSave, openPanel, setCanvasBg, setSnap, setShowGuides,
  type PanelKey, type CanvasBg
} from '../store/store';
import { navigate } from '../router';
import { Icon } from '../ui/icons';
import { NumberField, Stepper, Switch, Check, Seg, MenuButton, MenuItem, Dialog } from '../ui/primitives';
import { DpiBadge } from '../ui/quality';
import { FilePicker, useFileDrop } from '../ui/files';
import { useEstimate } from '../ui/Panels';
import { HelpDialog } from '../ui/Header';
import { textSpecOf } from '../text/textSpec';
import { SheetChoiceSelect, TransferSeg } from './controls';
import { FillDialog } from './Sidebar';
import { BACKGROUNDS } from './CanvasArea';

const TextPanel = lazy(() => import('./TextPanel'));

export function PanelView({ panel }: { panel: PanelKey }) {
  switch (panel) {
    case 'upload': return <UploadPanel />;
    case 'designs': return <DesignsPanel />;
    case 'text': return <Suspense fallback={<PanelShell title="Add text"><div className="pnl-b"><span className="spin" /></div></PanelShell>}><TextPanel /></Suspense>;
    case 'nest': return <NestPanel />;
    case 'bg': return <BgPanel />;
    case 'settings': return <SettingsPanel />;
  }
}

export function PanelShell({ title, sub, actions, children, foot }: { title: string; sub?: string; actions?: ReactNode; children: ReactNode; foot?: ReactNode }) {
  return (
    <div className="pnl">
      <div className="pnl-h">
        <div className="grow"><h2>{title}</h2>{sub && <p>{sub}</p>}</div>
        {actions}
        <button className="ibtn pnl-x" aria-label="Close panel" data-tip="Close" data-tip-pos="left" onClick={() => useStore.setState({ panel: null })}><Icon name="chevLeft" /></button>
      </div>
      <div className="pnl-b">{children}</div>
      {foot && <div className="pnl-f">{foot}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- upload */
function UploadPanel() {
  const cfg = useStore(s => s.config)!;
  const uploads = useStore(s => s.uploads);
  const drop = useFileDrop();
  const busy = Object.values(uploads).filter(u => u.status !== 'ready');
  const widthCm = Math.round(cfg.pricing.transfers.dtf.byLength.widthMm / 10);
  return (
    <PanelShell title="Upload" sub="Add your artwork. We trim empty space and check the quality.">
      <div className={'drop drop-sm' + (drop.over ? ' is-over' : '')} {...drop.bind}>
        <span className="drop-ico"><Icon name="upload" /></span>
        <b>Drop your designs here</b>
        <span className="small muted">PNG with transparent backgrounds works best.</span>
        <FilePicker className="btn btn-primary btn-sm">Choose files</FilePicker>
        <span className="types">PNG · JPG · WEBP · SVG · PDF · TIFF · up to {cfg.builder.upload.maxFileMb} MB each</span>
      </div>
      {busy.length > 0 && (
        <ul className="uplist" aria-label="Uploads">
          {busy.map(u => (
            <li key={u.designId} className={u.status === 'failed' ? 'is-bad' : ''}>
              <Icon name={u.status === 'failed' ? 'alert' : 'file'} />
              <span className="grow"><b>{u.name}</b><small>{u.status === 'failed' ? u.error || 'Couldn’t read this file' : u.status === 'queued' ? 'Waiting…' : u.status === 'uploading' ? 'Uploading… ' + Math.round(u.progress * 100) + '%' : 'Checking artwork quality…'}</small>
                {(u.status === 'uploading' || u.status === 'queued') && <span className="bar"><i style={{ width: Math.round(u.progress * 100) + '%' }} /></span>}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="callout callout-info callout-sm"><Icon name="wand" /><div>Got a white or coloured background around your design? <button className="link" onClick={() => openPanel('bg')}>Background removal is coming soon</button>. For now, a PNG with a transparent background prints just the design.</div></div>
      <a className="link small" href={cfg.links.uploadPage}>Upload a finished sheet instead <Icon name="arrowRight" /></a>
      <ul className="assure">
        <li><Icon name="check" />{widthCm} cm print width</li>
        <li><Icon name="check" />No minimums</li>
        <li><Icon name="check" />Bulk pricing</li>
        <li><Icon name="check" />Printed here in Aotearoa</li>
      </ul>
    </PanelShell>
  );
}

/* --------------------------------------------------------------- designs */
function DesignsPanel() {
  const doc = useStore(s => s.doc);
  const sel = useStore(s => s.designSel);
  const assets = useStore(s => s.assets);
  const unit = doc.settings.unit;
  const allSel = doc.designs.length > 0 && sel.length === doc.designs.length;
  const setSel = (ids: string[]) => useStore.setState({ designSel: ids });
  const transfers = requestedCount(doc);
  const waiting = doc.designs.filter(d => d.assetId.startsWith('pending:') || assets[d.assetId]?.status === 'processing').length;
  const built = doc.sheets.length > 0;
  return (
    <PanelShell title={'Designs (' + doc.designs.length + ')'} sub="Set the width and how many you need."
      actions={<FilePicker className="ibtn" label="Upload designs" tip="Upload more"><Icon name="upload" /></FilePicker>}
      foot={doc.designs.length > 0 && (
        <>
          <div className="pnl-sum"><span><b className="num">{transfers}</b> transfer{transfers === 1 ? '' : 's'}</span><span className="muted">{waiting ? 'Uploading ' + waiting + '…' : doc.designs.length + ' design' + (doc.designs.length === 1 ? '' : 's')}</span></div>
          {/* before the first build, wide screens show the big Build button on the canvas beside this panel;
              where the panel covers the canvas (tablets, phones) the button is here too */}
          <button className={'btn btn-primary btn-block' + (built ? '' : ' hide-wide')} disabled={!transfers || waiting > 0} onClick={() => void build('build')}><Icon name="build" />{built ? 'Rebuild my gangsheet' : 'Build my gangsheet'}</button>
        </>
      )}>
      <div className="dhead">
        {doc.designs.length > 0 && <Check checked={allSel} indeterminate={sel.length > 0 && !allSel} onChange={v => setSel(v ? doc.designs.map(d => d.id) : [])} label="Select all designs" />}
        <Seg<Unit> label="Units" value={unit} onChange={setUnit} options={[{ value: 'cm', label: 'cm' }, { value: 'mm', label: 'mm' }, { value: 'in', label: 'in' }]} />
        <span className="grow" />
        {doc.designs.length > 0 && <button className="btn btn-soft btn-xs" onClick={() => autoSize(sel.length ? sel : doc.designs.map(d => d.id))} data-tip="Size each design for the sharpest print (300 DPI)" data-tip-pos="left"><Icon name="autosize" />Best size{sel.length ? '' : ' for all'}</button>}
      </div>
      {sel.length > 0 && <BulkBar ids={sel} unit={unit} onDone={() => setSel([])} />}
      <ul className="dlist">
        {doc.designs.map(d => <DesignRow key={d.id} d={d} selected={sel.includes(d.id)} onSelect={v => setSel(v ? [...sel, d.id] : sel.filter(x => x !== d.id))} />)}
      </ul>
      {!doc.designs.length && (
        <div className="pnl-empty">
          <Icon name="image" />
          <b>No designs yet</b>
          <span>Upload your artwork, or add some text.</span>
          <div className="row-btns" style={{ justifyContent: 'center' }}>
            <FilePicker className="btn btn-primary btn-sm"><Icon name="upload" />Upload</FilePicker>
            <button className="btn btn-line btn-sm" onClick={() => openPanel('text')}><Icon name="type" />Add text</button>
          </div>
        </div>
      )}
      {doc.designs.length > 0 && <FilePicker className="add-more"><Icon name="plus" />Add more designs</FilePicker>}
      {doc.designs.length > 0 && <p className="tiny muted pnl-hint">Tip: drag a design onto the sheet to place a copy by hand.</p>}
    </PanelShell>
  );
}

function DesignRow({ d, selected, onSelect }: { d: Design; selected: boolean; onSelect: (v: boolean) => void }) {
  const asset = useStore(s => s.assets[d.assetId]);
  const up = useStore(s => s.uploads[d.id]);
  const cfg = useStore(s => s.config)!;
  const unit = useStore(s => s.doc.settings.unit);
  const open = useStore(s => s.designOpen.includes(d.id));
  const lock = useStore(s => s.lockAspect);
  const built = useStore(s => s.doc.sheets.length > 0);
  const placed = useStore(s => s.doc.sheets.reduce((n, sh) => n + sh.items.filter(i => i.designId === d.id).length, 0));
  const b = cfg.builder;
  const pending = d.assetId.startsWith('pending:') || asset?.status === 'processing';
  const failed = up?.status === 'failed' || asset?.status === 'failed';
  const ready = asset?.status === 'ready';
  const thumb = asset?.thumbUrl || up?.localUrl;
  const q = ready ? effectiveDpi(asset!, d.trim, d.widthMm, d.heightMm, b) : null;
  const maxW = cfg.pricing.transfers[useStore.getState().doc.transfer].byLength.widthMm - 2 * b.edgeMm;
  const tooWide = Math.min(d.widthMm, d.heightMm) > maxW + 0.01;
  const isText = !!textSpecOf(d);
  let fileInfo = '';
  if (ready && asset) {
    fileInfo = asset.kind === 'raster'
      ? Math.round(asset.widthPx * (d.trim ? asset.trim.w : 1)) + ' × ' + Math.round(asset.heightPx * (d.trim ? asset.trim.h : 1)) + ' px ' + asset.mime.split('/')[1].toUpperCase().replace('JPEG', 'JPG')
      : isText ? 'Text (sharp at any size)' : (asset.kind === 'pdf' ? 'PDF' : 'SVG') + ' (sharp at any size)';
  }
  const short = built && placed < d.quantity;
  // stretched: the design's proportions no longer match the artwork's (set with the lock off)
  const stretch = ready && asset ? (d.widthMm / d.heightMm) / aspectOf(d) : 1;
  const stretched = Math.abs(stretch - 1) > 0.01;
  return (
    <li className={'drow' + (selected ? ' is-sel' : '') + (failed || tooWide ? ' is-bad' : '') + (open ? ' is-open' : '')}>
      <div className="drow-top">
        <Check checked={selected} onChange={onSelect} label={'Select ' + d.name} />
        <div className="thumb" draggable={ready} data-tip={ready ? 'Drag onto the sheet' : undefined} data-tip-pos="right"
          onDragStart={e => { e.dataTransfer.setData('application/x-gsb-design', d.id); e.dataTransfer.effectAllowed = 'copy'; }}>
          {thumb ? <img src={thumb} alt="" draggable={false} /> : <Icon name="image" width={22} height={22} style={{ color: '#9AAAB5' }} />}
          {up && (up.status === 'uploading' || up.status === 'queued') && <div className="up"><div className="bar"><i style={{ width: Math.round(up.progress * 100) + '%' }} /></div></div>}
        </div>
        <div className="drow-info">
          <input className="drow-name" aria-label="Design name" title={d.name} value={d.name} onChange={e => renameDesign(d.id, e.target.value)} />
          <div className="drow-meta">
            {failed ? <span className="drow-err">{up?.error || asset?.error || 'This file couldn’t be read.'}</span>
              : up?.status === 'queued' ? <span>Waiting to upload…</span>
              : up?.status === 'uploading' ? <span>Uploading… {Math.round(up.progress * 100)}%</span>
              : pending ? <span className="drow-wait"><span className="spin" />Checking quality…</span>
              : <>
                  <DpiBadge asset={asset} design={d} builder={b} unit={unit} />
                  {built && <span className={short ? 'is-short' : ''}>{placed} of {d.quantity} placed</span>}
                </>}
          </div>
        </div>
        <MenuButton align="right" button={(o, toggle) => (
          <button className="ibtn ibtn-sm" aria-label={'More for ' + d.name} aria-expanded={o} onClick={toggle}><Icon name="more" /></button>
        )}>{close => (
          <>
            <MenuItem icon="plus" label="Add a copy to the sheet" disabled={!ready} onClick={() => { void addCopy(d.id); close(); }} />
            {isText && <MenuItem icon="type" label="Edit text" onClick={() => { useStore.setState({ textEdit: d.id, panel: 'text' }); close(); }} />}
            <MenuItem icon="copy" label="Duplicate design" onClick={() => { duplicateDesigns([d.id]); close(); }} />
            <hr />
            <MenuItem icon="trash" danger label="Remove" onClick={() => { removeDesigns([d.id]); close(); }} />
          </>
        )}</MenuButton>
      </div>
      <div className={'drow-ctrl' + (lock ? '' : ' is-free')}>
        <NumberField label="Width" unit={unit} mmValue value={d.widthMm} min={5} max={5000} disabled={failed} onCommit={v => setDesignSize([d.id], v, null)} small />
        <button type="button" className="drow-lock" aria-pressed={lock} aria-label={lock ? 'Proportions locked' : 'Proportions unlocked'} onClick={() => setLockAspect(!lock)}
          data-tip={lock ? 'Locked: the height follows the width. Click to set them separately' : 'Unlocked: width and height are separate. Click to lock'} data-tip-pos="up">
          <Icon name={lock ? 'lock' : 'unlock'} />
        </button>
        {lock
          ? <span className="drow-h" title="Height (follows the width)">{formatLength(d.heightMm, unit)}</span>
          : <NumberField label="Height" unit={unit} mmValue value={d.heightMm} min={5} max={5000} disabled={failed} onCommit={v => setDesignSize([d.id], null, v)} small />}
        <Stepper label="Quantity" value={d.quantity} min={0} max={10000} onCommit={v => setQuantity([d.id], v)} small />
      </div>
      {stretched && <div className="drow-note"><Icon name="info" /><span>{stretchText(stretch)} <button className="link" onClick={() => resetDesignProportions([d.id])}>Reset proportions</button></span></div>}
      {q?.quality === 'low' && <div className="drow-warn"><Icon name="alert" /><span>May look pixelated at this size. <button className="link" onClick={() => autoSize([d.id])}>Use {formatLength(round1(d.widthMm * q.dpi / b.dpiGood), unit)} wide</button> for a sharp print.</span></div>}
      {ready && asset?.opaque && <div className="drow-warn"><Icon name="alert" /><span>Solid background: the whole rectangle will print.</span></div>}
      {tooWide && <div className="drow-warn drow-err"><Icon name="alert" /><span>Too big for the sheet: the most is {formatLength(maxW, unit)} across.</span></div>}
      <button type="button" className="drow-more" aria-expanded={open} onClick={() => toggleDesignOpen(d.id)}>
        <Icon name="chevDown" className={'chev' + (open ? '' : ' is-shut')} />{open ? 'Fewer options' : 'More options'}
      </button>
      {open && (
        <div className="drow-adv">
          <div className="drow-adv-row">
            {lock
              ? <NumberField label="Or set the height" unit={unit} mmValue value={d.heightMm} min={5} max={5000} disabled={failed} onCommit={v => setDesignSize([d.id], null, v)} small />
              : <span className="tiny muted">Width and height are set separately (proportions unlocked).</span>}
            <button className="btn btn-soft btn-xs" disabled={!ready} onClick={() => autoSize([d.id])} data-tip="The biggest size that still prints sharp (300 DPI), in the artwork’s own proportions" data-tip-pos="up"><Icon name="autosize" />Best size</button>
          </div>
          {fileInfo && <p className="tiny muted">{fileInfo}</p>}
          <Switch checked={d.trim} onChange={v => setDesignFlag(d.id, 'trim', v)}>Trim empty space around the design</Switch>
          <Switch checked={!d.lockRotation} onChange={v => setDesignFlag(d.id, 'lockRotation', !v)}>Allow turning it to save film</Switch>
        </div>
      )}
    </li>
  );
}

function BulkBar({ ids, unit, onDone }: { ids: string[]; unit: Unit; onDone: () => void }) {
  const [w, setW] = useState('');
  const [q, setQ] = useState('');
  const dec = unitDecimals(unit);
  const applyW = () => {
    const v = parseFloat(w.replace(',', '.'));
    if (!(v > 0)) return;
    const mm = unit === 'cm' ? v * 10 : unit === 'in' ? v * 25.4 : v;
    setDesignSize(ids, mm, null, 'Set same width');
    setW('');
  };
  const applyQ = () => { const v = parseInt(q, 10); if (v >= 0) { setQuantity(ids, v); setQ(''); } };
  return (
    <div className="bulk" role="toolbar" aria-label="Change selected designs">
      <div className="bulk-h"><b>{ids.length} selected</b><button className="ibtn ibtn-sm" aria-label="Clear selection" onClick={onDone}><Icon name="x" /></button></div>
      <div className="bulk-row">
        <span className="unit" data-unit={unit}><input className="in in-sm num" placeholder="Width" inputMode="decimal" value={w} step={dec ? 1 / Math.pow(10, dec) : 1}
          onChange={e => setW(e.target.value)} onKeyDown={e => e.key === 'Enter' && applyW()} aria-label="Same width for all selected" /></span>
        <button className="btn btn-soft btn-xs" onClick={applyW}>Set same width</button>
      </div>
      <div className="bulk-row">
        <input className="in in-sm num" placeholder="Qty" inputMode="numeric" value={q} onChange={e => setQ(e.target.value.replace(/[^\d]/g, ''))} onKeyDown={e => e.key === 'Enter' && applyQ()} aria-label="Same quantity for all selected" />
        <button className="btn btn-soft btn-xs" onClick={applyQ}>Set same quantity</button>
      </div>
      <div className="bulk-row">
        <button className="btn btn-soft btn-xs" onClick={() => autoSize(ids)}><Icon name="autosize" />Best size</button>
        <button className="btn btn-soft btn-xs" onClick={() => duplicateDesigns(ids)}><Icon name="copy" />Duplicate</button>
        <button className="btn btn-danger btn-xs" onClick={() => { removeDesigns(ids); onDone(); }}><Icon name="trash" />Delete</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- auto nest */
function NestPanel() {
  const doc = useStore(s => s.doc);
  const cfg = useStore(s => s.config)!;
  const assets = useStore(s => s.assets);
  const uploads = useStore(s => s.uploads);
  const est = useEstimate();
  const [renestAsk, setRenestAsk] = useState(false);
  const [fill, setFill] = useState(false);
  const b = cfg.builder;
  const transfers = requestedCount(doc);
  const waiting = doc.designs.filter(d => d.assetId.startsWith('pending:') || assets[d.assetId]?.status === 'processing').length;
  const failed = doc.designs.filter(d => uploads[d.id]?.status === 'failed' || assets[d.assetId]?.status === 'failed').length;
  const low = doc.designs.filter(d => { const a = assets[d.assetId]; return a?.status === 'ready' && effectiveDpi(a, d.trim, d.widthMm, d.heightMm, b).quality === 'low'; }).length;
  const built = doc.sheets.length > 0;
  const placed = doc.sheets.reduce((n, s) => n + s.items.length, 0);
  return (
    <PanelShell title="Auto nest" sub={doc.settings.sheet.mode === 'auto' ? 'We arrange everything to use the least film and pick the cheapest sheet.' : 'We arrange everything to use the least film, on the sheet you chose.'}
      foot={(
        <>
          <div className="pnl-sum">
            <span><b className="num">{transfers}</b> transfer{transfers === 1 ? '' : 's'}{est && !est.built && est.lengthMm ? ' · about ' + formatMetres(est.lengthMm) : ''}</span>
            {est && <span><span className="muted">{est.built ? 'Price' : 'Estimate'}</span> <b className="num">{formatMoney(est.total)}</b></span>}
          </div>
          <button className="btn btn-primary btn-block" disabled={!transfers || waiting > 0} onClick={() => void build('build')}><Icon name="build" />{built ? 'Rebuild my gangsheet' : 'Build my gangsheet'}</button>
        </>
      )}>
      <div className="stack">
        <div className="field"><span className="label">Printing</span><TransferSeg size="md" /></div>
        <SheetChoiceSelect />
        <div className="grid2">
          {b.allowCustomerGap
            ? <NumberField label="Space between" unit="mm" value={doc.settings.gapMm} decimals={1} min={Math.max(b.minGapMm, inkGapFor(doc, b))} max={50} onCommit={setGap} />
            : <div className="field"><span className="label">Space between</span><b style={{ lineHeight: '40px' }}>{doc.settings.gapMm} mm</b></div>}
          <div className="field"><span className="label">Rotate to save film</span><div style={{ height: 40, display: 'flex', alignItems: 'center' }}><Switch checked={doc.settings.allowRotation} onChange={setAllowRotation}>{doc.settings.allowRotation ? 'On' : 'Off'}</Switch></div></div>
        </div>
        <p className="tiny muted">Edge margin {b.edgeMm} mm · at least {inkGapFor(doc, b)} mm between printed artwork · 300 DPI print file.</p>
      </div>
      {waiting > 0 && <div className="callout callout-info"><span className="spin" />{waiting === 1 ? '1 design is' : waiting + ' designs are'} still uploading.</div>}
      {failed > 0 && <div className="callout callout-bad"><Icon name="alert" />{failed === 1 ? '1 file' : failed + ' files'} couldn’t be read. Remove {failed === 1 ? 'it' : 'them'} in Designs.</div>}
      {low > 0 && <div className="callout callout-warn"><Icon name="alert" />{low === 1 ? '1 design' : low + ' designs'} may look pixelated at the size you chose.</div>}
      <div className="pnl-sec">
        <span className="label">Layout tools</span>
        <div className="toolgrid">
          <button className="tool" disabled={!placed} onClick={() => setRenestAsk(true)}><Icon name="renest" /><b>Re-nest</b><span>Rearrange what’s on the sheets, keeping each size</span></button>
          <button className="tool" disabled={!built} onClick={() => setFill(true)}><Icon name="fill" /><b>Fill empty space</b><span>Add copies into the gaps</span></button>
          <button className="tool" onClick={() => { addSheet(); }}><Icon name="plus" /><b>Add a sheet</b><span>A blank sheet to place designs by hand</span></button>
        </div>
      </div>
      {renestAsk && (
        <Dialog title="Re-nest gangsheet" onClose={() => setRenestAsk(false)}>
          <span className="kicker">Re-nest</span>
          <h2>Rearrange everything?</h2>
          <p className="muted">This rearranges your current layout to use the least film. Each copy keeps its size. You can undo it.</p>
          <div className="dialog-actions">
            <button className="btn btn-line btn-sm" onClick={() => setRenestAsk(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={() => { setRenestAsk(false); void build('renest'); }}><Icon name="renest" />Re-nest</button>
          </div>
        </Dialog>
      )}
      {fill && <FillDialog onClose={() => setFill(false)} />}
    </PanelShell>
  );
}

/* ------------------------------------------------- background (coming soon) */
function BgPanel() {
  const links = useStore(s => s.config?.links);
  return (
    <PanelShell title="Background removal">
      <div className="soon">
        <span className="soon-ico"><Icon name="wand" /></span>
        <span className="chip chip-amber">Coming soon</span>
        <h3>Remove backgrounds in the builder</h3>
        <p>We’re working on one-click background removal. For now, upload a PNG with a transparent background: it prints just the design.</p>
        <p>Designs with a solid background are flagged in your checks before you order, so nothing prints as a box by surprise.</p>
        {links && <a className="link small" href={links.guide} target="_blank" rel="noreferrer">How to make a transparent PNG <Icon name="arrowRight" /></a>}
      </div>
    </PanelShell>
  );
}

/* --------------------------------------------------------------- settings */
function SettingsPanel() {
  const snap = useStore(s => s.snap);
  const show = useStore(s => s.show);
  const canvasBg = useStore(s => s.canvasBg);
  const unit = useStore(s => s.doc.settings.unit);
  const name = useStore(s => s.doc.name);
  const [help, setHelp] = useState(false);
  // remembered in this browser
  const setSnapOpt = (k: keyof typeof snap, v: boolean) => setSnap({ [k]: v });
  const setShow = (k: keyof typeof show, v: boolean) => (k === 'guides' ? setShowGuides(v) : useStore.setState(s => ({ show: { ...s.show, [k]: v } })));
  return (
    <PanelShell title="Settings">
      <div className="field name-alt" style={{ marginBottom: 14 }}>
        <span className="label">Gangsheet name</span>
        <input className="in" aria-label="Gangsheet name (settings)" value={name} maxLength={120} onChange={e => rename(e.target.value)} onBlur={() => void flushSave()} />
      </div>
      <div className="field"><span className="label">Units</span><Seg<Unit> label="Units" value={unit} onChange={setUnit} options={[{ value: 'cm', label: 'Centimetres' }, { value: 'mm', label: 'mm' }, { value: 'in', label: 'Inches' }]} /></div>
      <div className="pnl-sec">
        <span className="label">Snapping</span>
        <div className="setlist">
          <Switch checked={snap.objects} onChange={v => setSnapOpt('objects', v)}>Snap to designs and gaps</Switch>
          <Switch checked={snap.canvas} onChange={v => setSnapOpt('canvas', v)}>Snap to the margins and centre</Switch>
          <Switch checked={snap.grid} onChange={v => setSnapOpt('grid', v)}>Snap to a {snap.gridMm} mm grid (shown on the sheet)</Switch>
          <p className="tiny muted">Snapping works when moving and resizing. Hold Alt (Option on a Mac) while moving to place freely.</p>
        </div>
      </div>
      <div className="pnl-sec">
        <span className="label">Show</span>
        <div className="setlist">
          <Switch checked={show.rulers} onChange={v => setShow('rulers', v)}>Rulers</Switch>
          <Switch checked={show.guides} onChange={v => setShow('guides', v)}>Always show the edge margin</Switch>
          <Switch checked={show.minimap} onChange={v => setShow('minimap', v)}>Mini-map on long sheets</Switch>
        </div>
      </div>
      <div className="pnl-sec">
        <span className="label">Sheet background</span>
        <Seg<CanvasBg> label="Sheet background" value={canvasBg} onChange={setCanvasBg} options={BACKGROUNDS.map(b => ({ value: b.value, label: b.label }))} />
        <p className="tiny muted">Only changes the view. Your print file always prints just the artwork, on clear film.</p>
      </div>
      <div className="pnl-sec">
        <span className="label">Coming soon</span>
        <button className="btn btn-line btn-sm btn-block" onClick={() => openPanel('bg')}><Icon name="wand" />Background removal</button>
      </div>
      <div className="pnl-sec">
        <span className="label">Help</span>
        <div className="row-btns">
          <button className="btn btn-line btn-sm" onClick={() => setHelp(true)}><Icon name="help" />How it works &amp; shortcuts</button>
          <button className="btn btn-line btn-sm" onClick={() => navigate('/my')}><Icon name="sheets" />My gang sheets</button>
        </div>
      </div>
      {help && <HelpDialog onClose={() => setHelp(false)} />}
    </PanelShell>
  );
}

